import { isAxiosError } from 'axios'
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  parseFilenameFromContentDisposition,
  triggerBlobDownload,
} from '@/shared/lib/downloadBlob'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'
import { assertUploadFileSize } from '@/shared/lib/uploadGuardrails'
import { PACKAGING_SUPPLIERS_KEY } from './packagingSuppliers'

export type PackagingShipmentPageInfo = {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export type PackagingShipmentListItem = {
  id: string
  name: string
  note: string | null
  skuCount: number
  latestActiveUser: string
  skusUpdatedAt: string
  createdAt: string
  status: 'Checked' | 'Updating'
  lastCheckedAt: string | null
  attentionCount: number | null
  declarationsReadyCount: number | null
}

export type PackagingShipmentDisplayedCheck = {
  id: string
  finishedAt: string
  skuCount: number
  notInLexCount: number
  packedItemCount: number
  packagingItemsClearCount: number
  packagingItemsRequiringAttentionCount: number
  declarationsReadyCount: number
}

export type PackagingShipmentDetail = PackagingShipmentListItem & {
  checkRunning: boolean
  failureMessage: string | null
  displayedCheck: PackagingShipmentDisplayedCheck | null
}

export type PackagingShipmentsListResponse = {
  items: PackagingShipmentListItem[]
  pageInfo: PackagingShipmentPageInfo
}

export type PackagingShipmentsListQuery = {
  page?: number
  pageSize?: number
  sortBy?: 'name' | 'skusUpdatedAt' | 'createdAt'
  sortDir?: 'asc' | 'desc'
}

export type PackagingShipmentOrgStats = {
  total: number
  updating: number
  updatingPercent: number
  awaitingFirstResult: number
  awaitingFirstResultPercent: number
}

const PACKAGING_SHIPMENTS_KEY = [PACKAGING_SUPPLIERS_KEY, 'shipments'] as const

export function useListPackagingShipmentStats(enabled = true) {
  return useQuery({
    queryKey: [...PACKAGING_SHIPMENTS_KEY, 'stats'],
    queryFn: async () => {
      const response = await apiClient.get<PackagingShipmentOrgStats>(
        '/v1/packaging-suppliers/shipments/stats'
      )
      return response.data
    },
    enabled,
    refetchInterval: (query) =>
      (query.state.data?.updating ?? 0) > 0 ? 2000 : false,
  })
}

export function useListPackagingShipments(
  query: PackagingShipmentsListQuery,
  enabled = true
) {
  return useQuery({
    queryKey: [...PACKAGING_SHIPMENTS_KEY, query],
    queryFn: async () => {
      const response = await apiClient.get<PackagingShipmentsListResponse>(
        '/v1/packaging-suppliers/shipments',
        {
          params: {
            page: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            sortBy: query.sortBy ?? 'createdAt',
            sortDir: query.sortDir ?? 'desc',
          },
        }
      )
      return response.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })
}

export type CreatePackagingShipmentPayload = {
  name: string
  note?: string | null
  skus?: string[]
}

export interface ShipmentSkuFileColumn {
  index: number
  header: string
}

export interface ShipmentSkuFileNeedsColumn {
  outcome: 'needsColumn'
  columns: ShipmentSkuFileColumn[]
  sampleRows: string[][]
}

export interface ShipmentSkuFileExtracted {
  outcome: 'extracted'
  skus: string[]
  items: PackagingShipmentSkuReviewItem[]
}

export type ShipmentSkuFileParseResult =
  | ShipmentSkuFileNeedsColumn
  | ShipmentSkuFileExtracted

export type PackagingShipmentSkuReviewItem = {
  sku: string
  matched: boolean
}

export type ShipmentSkuFilePhase = 'uploading' | 'reading'

export type ParsePackagingShipmentSkuFileInput =
  | {
      file: File
      onPhaseChange?: (phase: ShipmentSkuFilePhase) => void
    }
  | { fileKey: string; skuColumn: number }

export type ShipmentSkuFileParseResponse = ShipmentSkuFileParseResult & {
  fileKey: string
}

const SHIPMENT_SKU_FILE_UPLOAD_CONTENT_TYPE = 'application/octet-stream'

export async function parsePackagingShipmentSkuFile(
  input: ParsePackagingShipmentSkuFileInput
): Promise<ShipmentSkuFileParseResponse> {
  if ('fileKey' in input) {
    const response = await apiClient.post<ShipmentSkuFileParseResult>(
      '/v1/packaging-suppliers/shipments/sku-file/parse',
      { fileKey: input.fileKey, skuColumn: input.skuColumn },
      { timeout: 120_000 }
    )
    return { ...response.data, fileKey: input.fileKey }
  }

  assertUploadFileSize(input.file)
  input.onPhaseChange?.('uploading')
  const upload = await apiClient.post<{ uploadUrl: string; fileKey: string }>(
    '/v1/packaging-suppliers/shipments/sku-file/upload-url',
    { fileName: input.file.name, fileSizeBytes: input.file.size }
  )
  await uploadFileToSignedOssUrl(upload.data.uploadUrl, input.file, {
    contentType: SHIPMENT_SKU_FILE_UPLOAD_CONTENT_TYPE,
    context: {
      feature: 'packaging-shipment-sku-file',
      fileName: input.file.name,
      fileSizeBytes: input.file.size,
      contentType: SHIPMENT_SKU_FILE_UPLOAD_CONTENT_TYPE,
    },
  })
  input.onPhaseChange?.('reading')
  const response = await apiClient.post<ShipmentSkuFileParseResult>(
    '/v1/packaging-suppliers/shipments/sku-file/parse',
    { fileKey: upload.data.fileKey },
    { timeout: 120_000 }
  )
  return { ...response.data, fileKey: upload.data.fileKey }
}

export function useParsePackagingShipmentSkuFile() {
  return useMutation({
    mutationFn: parsePackagingShipmentSkuFile,
  })
}

export function useReviewPackagingShipmentSkus() {
  return useMutation({
    mutationFn: async (payload: { skus: string[] }) => {
      const response = await apiClient.post<{
        items: PackagingShipmentSkuReviewItem[]
      }>('/v1/packaging-suppliers/shipments/sku-matches', payload, {
        timeout: 120_000,
      })
      return response.data
    },
  })
}

export function useGetPackagingShipment(shipmentId: string) {
  return useQuery({
    queryKey: [...PACKAGING_SHIPMENTS_KEY, shipmentId],
    queryFn: async () => {
      const response = await apiClient.get<PackagingShipmentDetail>(
        `/v1/packaging-suppliers/shipments/${shipmentId}`
      )
      return response.data
    },
    enabled: shipmentId.length > 0,
    refetchInterval: (query) =>
      query.state.data?.checkRunning === true ? 2000 : false,
  })
}

export type PackagingShipmentResultRow = {
  sku: string
  type: 'NOT_IN_LEX' | 'NO_PACKAGING' | 'PACKAGING_ITEM'
  packedItemId: string | null
  packedItemName: string | null
  supplierName: string | null
  packagingItemId: string | null
  packagingItemName: string | null
  declarationGenerated: boolean
  dataCheckLabel: string
  dataCheckDetail: string
  heavyMetalsLabel: string
  heavyMetalsDetail: string
  svhcLabel: string
  svhcDetail: string
}

export type PackagingShipmentResult = {
  finishedAt: string | null
  skuCount: number
  notInLexCount: number
  packedItemCount: number
  packagingItemsClearCount: number
  packagingItemsRequiringAttentionCount: number
  declarationsReadyCount: number
  rows: PackagingShipmentResultRow[]
  pageInfo: PackagingShipmentPageInfo
}

export type PackagingShipmentResultStatus =
  | 'attention'
  | 'notInLex'
  | 'noPackaging'
  | 'declarationMissing'

export const PACKAGING_EPR_MATERIALS = [
  'PLASTIC',
  'PAPER_CARDBOARD',
  'GLASS',
  'METAL_ALUMINIUM',
  'METAL_STEEL',
  'WOOD',
  'OTHER',
] as const

export type PackagingEprMaterial = (typeof PACKAGING_EPR_MATERIALS)[number]

export type PackagingShipmentEprCard = {
  material: PackagingEprMaterial
  grams: number
}

export type PackagingShipmentEprWeights = Partial<
  Record<PackagingEprMaterial, number>
>

export type PackagingShipmentEprComponent = {
  name: string
  material: PackagingEprMaterial
  grams: number | null
}

export type PackagingShipmentEprPackagingItem = {
  id: string
  name: string
  supplierId: string
  supplierName: string
  weights: PackagingShipmentEprWeights
  components: PackagingShipmentEprComponent[]
}

export type PackagingShipmentEprPackedItem = {
  id: string
  name: string
  sku: string
  weights: PackagingShipmentEprWeights
  packagingItems: PackagingShipmentEprPackagingItem[]
}

export type PackagingShipmentEpr = {
  cards: PackagingShipmentEprCard[]
  packedItems: PackagingShipmentEprPackedItem[]
}

export function useGetPackagingShipmentEpr(
  shipmentId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: [...PACKAGING_SHIPMENTS_KEY, shipmentId, 'epr'],
    queryFn: async () => {
      const response = await apiClient.get<PackagingShipmentEpr>(
        `/v1/packaging-suppliers/shipments/${shipmentId}/epr`
      )
      return response.data
    },
    enabled: enabled && shipmentId.length > 0,
  })
}

export function useGetPackagingShipmentResult(
  shipmentId: string,
  displayedCheckId: string | null,
  page: number,
  pageSize: number,
  filters: {
    status?: PackagingShipmentResultStatus
    skuQuery?: string
  },
  enabled: boolean
) {
  return useQuery({
    queryKey: [
      ...PACKAGING_SHIPMENTS_KEY,
      shipmentId,
      'result',
      displayedCheckId,
      page,
      pageSize,
      filters.status ?? '',
      filters.skuQuery ?? '',
    ],
    queryFn: async () => {
      const response = await apiClient.get<PackagingShipmentResult>(
        `/v1/packaging-suppliers/shipments/${shipmentId}/result`,
        {
          params: {
            page,
            pageSize,
            status: filters.status,
            q: filters.skuQuery,
          },
        }
      )
      return response.data
    },
    enabled: enabled && shipmentId.length > 0,
    placeholderData: keepPreviousData,
  })
}

export function useStartPackagingShipmentCheck(shipmentId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.post(
        `/v1/packaging-suppliers/shipments/${shipmentId}/check`
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: PACKAGING_SHIPMENTS_KEY,
      })
    },
  })
}

export function useCreatePackagingShipment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePackagingShipmentPayload) => {
      const response = await apiClient.post<PackagingShipmentListItem>(
        '/v1/packaging-suppliers/shipments',
        payload,
        { timeout: 120_000 }
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: PACKAGING_SHIPMENTS_KEY,
      })
    },
  })
}

export async function messageFromShipmentFileError(
  error: unknown,
  fallback: string
): Promise<string> {
  if (!isAxiosError(error)) {
    return fallback
  }
  const data: unknown = error.response?.data
  if (
    data != null &&
    typeof data === 'object' &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.length > 0
  ) {
    return data.message
  }
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: unknown }
      if (typeof parsed.message === 'string' && parsed.message.length > 0) {
        return parsed.message
      }
    } catch {
      return fallback
    }
  }
  return fallback
}

async function downloadShipmentFile(
  path: string,
  fallbackFilename: string
): Promise<void> {
  const response = await apiClient.get<Blob>(path, {
    responseType: 'blob',
    timeout: 120_000,
  })
  const filename = parseFilenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFilename
  )
  triggerBlobDownload(response.data, filename)
}

export function useDownloadPackagingShipmentDeclarations(shipmentId: string) {
  return useMutation({
    mutationFn: () =>
      downloadShipmentFile(
        `/v1/packaging-suppliers/shipments/${shipmentId}/declarations`,
        'declarations.zip'
      ),
  })
}

export function useExportPackagingShipmentSpreadsheet(shipmentId: string) {
  return useMutation({
    mutationFn: () =>
      downloadShipmentFile(
        `/v1/packaging-suppliers/shipments/${shipmentId}/spreadsheet`,
        'shipment.xlsx'
      ),
  })
}

export function useExportPackagingShipmentEprSpreadsheet(shipmentId: string) {
  return useMutation({
    mutationFn: () =>
      downloadShipmentFile(
        `/v1/packaging-suppliers/shipments/${shipmentId}/epr/spreadsheet`,
        'shipment-epr.xlsx'
      ),
  })
}

export function useUpdatePackagingShipment(shipmentId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name: string
      note?: string | null
      skus?: string[]
      appendSkus?: string[]
    }) => {
      const response = await apiClient.patch<PackagingShipmentListItem>(
        `/v1/packaging-suppliers/shipments/${shipmentId}`,
        payload,
        { timeout: 120_000 }
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: PACKAGING_SHIPMENTS_KEY,
      })
    },
  })
}

export function useDeletePackagingShipment(shipmentId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/v1/packaging-suppliers/shipments/${shipmentId}`)
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: [...PACKAGING_SHIPMENTS_KEY, shipmentId],
      })
      void queryClient.invalidateQueries({
        queryKey: PACKAGING_SHIPMENTS_KEY,
      })
    },
  })
}
